import { useState, type ChangeEvent } from "react";

/** The message a value breaks (already translated), or `undefined` when it is acceptable. */
export type FieldValidator = (value: string) => string | undefined;

export interface FormFieldOptions {
  /** Checks the value; called on every render, so it may read other fields (a confirmation). */
  readonly validate?: FieldValidator;
  /** Normalises what the user types (digits only, no spaces) before it is kept. */
  readonly sanitize?: (raw: string) => string;
  readonly initial?: string;
}

/** The props a `TextField` takes from a field: value, visible message and the two handlers. */
export interface FormFieldInputProps {
  readonly value: string;
  readonly error: string | undefined;
  readonly onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly onBlur: () => void;
}

export interface FormField {
  readonly value: string;
  /** `true` when the validator accepts the current value (shown or not). */
  readonly valid: boolean;
  /**
   * The message to show: only once the user left the field after editing it, or after a submit
   * attempt (`reveal`). Never for a field nobody touched, and never right after `reset`.
   */
  readonly error: string | undefined;
  readonly change: (raw: string) => void;
  readonly blur: () => void;
  /** A submit attempt: the message, if any, shows from now on. */
  readonly reveal: () => void;
  /**
   * Back to the untouched state: value, edited and shown flags together, so an emptied field is
   * never shown as a blank entry. There is deliberately no way to clear only the value.
   */
  readonly reset: () => void;
  readonly inputProps: FormFieldInputProps;
}

interface FieldState {
  readonly value: string;
  readonly edited: boolean;
  readonly shown: boolean;
}

/**
 * One form field's state, owned in one place: the value, whether the user edited or left it, and
 * the validation message derived from both. Forms with password and code fields use it so that an
 * attempt, a refusal or a reset never flashes a "required" or "mismatch" message the user did not
 * cause (owner reports 2026-10-02 and 2026-10-03).
 */
export function useFormField(options: FormFieldOptions = {}): FormField {
  const { validate, sanitize, initial = "" } = options;
  const [state, setState] = useState<FieldState>({ value: initial, edited: false, shown: false });
  const message = validate?.(state.value);

  const change = (raw: string) => {
    const value = sanitize ? sanitize(raw) : raw;
    setState((previous) => ({ ...previous, value, edited: true }));
  };
  const blur = () => {
    setState((previous) =>
      previous.edited && !previous.shown ? { ...previous, shown: true } : previous,
    );
  };
  const reveal = () => {
    setState((previous) => (previous.shown ? previous : { ...previous, shown: true }));
  };
  const reset = () => {
    setState({ value: initial, edited: false, shown: false });
  };
  const error = state.shown ? message : undefined;

  return {
    value: state.value,
    valid: message === undefined,
    error,
    change,
    blur,
    reveal,
    reset,
    inputProps: {
      value: state.value,
      error,
      onChange: (event) => {
        change(event.target.value);
      },
      onBlur: blur,
    },
  };
}

/** A submit attempt over several fields: shows every message and tells whether all are valid. */
export function submitFields(...fields: readonly FormField[]): boolean {
  for (const field of fields) field.reveal();
  return fields.every((field) => field.valid);
}

/** Resets several fields at once (after a submit: nothing lingers, nothing is flagged). */
export function resetFields(...fields: readonly FormField[]): void {
  for (const field of fields) field.reset();
}
